/* ═══════════════════════════════════════════════════════════════
   REVIEW — a project forma's master updates (SYNC_PLAN §3.2).

   Same decisions / persist / walk as a spec's Review (useReviewCore); only
   Finish differs:

     1. planReviewForma(the forma as the shared draft holds it, the
        settings, the decisions) — accepted settings deep-set by path
     2. ONE transaction into the shared draft (applyFormaDiff) — the draft
        goes Pending publish; the PUBLISHED forma is untouched (guarantee 2:
        Accept lands in the draft, Publish stays the only road out)
     3. service.finish(rowId, decisions, the post-apply forma) — the server
        records the answers, advances the base, writes the History line
        (and keeps the forma as the draft itself when nobody has it open)
     4. toast "3 changes applied, 1 rejected" → leave Review

   Undecided settings stay pending on the row, untouched (guarantee 1).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type * as Y from 'yjs';
import type { StandardConfig } from '../../shared/types';
import {
  isFormaChange,
  planReviewForma,
  type Change,
  type Decision,
  type FormaChange,
} from '../../shared/sync';
import { applyFormaDiff } from '../forma-studio';
import { isAlreadyResolved } from './finishGuard';
import { finishSummary, pendingChangeCount, useReviewCore, type ChangeWalker } from './useReview';
import type { ReviewService, SyncFeedRow, UpstreamPending } from './types';

const FORMA_LIVE_TABLES = ['sync_feed', 'standard_configs'];

/** The words the History line carries ("Review: 3 accepted, 1 rejected from
 *  Firm Standard") — the same text the server writes (sync-rules). */
export function formaReviewNote(
  applied: number,
  rejected: number,
  label: string | null | undefined
): string {
  return `Review: ${applied} accepted, ${rejected} rejected from ${label || 'upstream'}`;
}

/**
 * The post-Accept forma: the accepted settings on the draft, plus the
 * History line (newest first). Pure — the hook and the tests share it.
 */
export function planFormaFinish(
  current: StandardConfig,
  items: Change[],
  decisions: Record<string, Decision>,
  who: { userName: string; userId?: string; label?: string | null; now?: string }
): { forma: StandardConfig; applied: number; rejected: number } {
  const settings = items.filter(isFormaChange) as FormaChange[];
  const plan = planReviewForma(current, settings, decisions);
  // Nothing accepted = nothing to write: the draft stays exactly as it was
  // (no History line here, no Pending publish). The server still records it.
  if (plan.applied === 0) return { ...plan, forma: current };
  const entry = {
    userName: who.userName || 'Someone',
    ...(who.userId ? { userId: who.userId } : {}),
    timestamp: who.now || new Date().toISOString(),
    action: 'edited' as const,
    note: formaReviewNote(plan.applied, plan.rejected, who.label),
  };
  return {
    ...plan,
    forma: { ...plan.forma, editHistory: [entry, ...(current.editHistory || [])].slice(0, 20) },
  };
}

export interface UseFormaReviewOptions {
  /** The project forma (null = nothing open: the hook idles). */
  targetId: string | null;
  service: ReviewService;
  /** The project forma's shared draft (null until it is open). */
  ydoc: Y.Doc | null;
  /** The forma as the draft holds it right now (null until seeded). */
  current: StandardConfig | null;
  /** formas.publish — without it the walk is read-only. */
  canDecide: boolean;
  toast: (msg: string, isError?: boolean) => void;
  userName: string;
  userId?: string;
}

export interface FormaReviewApi {
  loading: boolean;
  /** The feed has answered for the current target. */
  loaded: boolean;
  loadError: string | null;
  rows: SyncFeedRow[];
  upstreamPending: UpstreamPending[];
  pendingCount: number;
  reload: () => void;
  active: boolean;
  start: (rowId?: string) => void;
  leave: () => void;
  row: SyncFeedRow | null;
  items: FormaChange[];
  reconciled: FormaChange[];
  decisions: Record<string, Decision>;
  walker: ChangeWalker;
  canDecide: boolean;
  decide: (id: string, d: Decision) => void;
  acceptAllRemaining: () => void;
  canUndoAcceptAll: boolean;
  undoAcceptAll: () => void;
  /** True when the review was recorded and left. */
  finish: () => Promise<boolean>;
  busy: boolean;
  error: string | null;
}

export function useFormaReview({
  targetId,
  service,
  ydoc,
  current,
  canDecide,
  toast,
  userName,
  userId,
}: UseFormaReviewOptions): FormaReviewApi {
  const core = useReviewCore({
    targetKind: 'project_forma',
    targetId,
    service,
    canDecide,
    liveTables: FORMA_LIVE_TABLES,
  });
  const { row, items, decisions, leave, setRows, setError, reload } = core;
  const [busy, setBusy] = React.useState(false);
  // A Finish whose accepted settings are already in the draft (the server
  // call failed): a retry records it without applying a second time.
  const appliedRef = React.useRef<{
    rowId: string;
    forma: StandardConfig;
    applied: number;
    rejected: number;
  } | null>(null);
  const formaItems = React.useMemo(() => items.filter(isFormaChange), [items]);
  const formaReconciled = React.useMemo(
    () => core.reconciled.filter(isFormaChange),
    [core.reconciled]
  );

  const finish = React.useCallback(async () => {
    if (!canDecide || !row) return false;
    if (!ydoc || !current) {
      const msg = 'The forma is still opening — try Finish again in a moment.';
      setError(msg);
      toast(msg, true);
      return false;
    }
    setBusy(true);
    setError(null);
    let wrote = false;
    try {
      let done = appliedRef.current?.rowId === row.id ? appliedRef.current : null;
      if (!done) {
        const plan = planFormaFinish(current, items, decisions, {
          userName,
          userId,
          label: row.upstreamLabel,
        });
        if (plan.applied > 0) applyFormaDiff(ydoc, current, plan.forma, 'review');
        done = { rowId: row.id, ...plan };
        appliedRef.current = done;
      }
      wrote = true;
      try {
        await service.finish(row.id, decisions, done.forma);
      } catch (err) {
        if (!isAlreadyResolved(err)) throw err;
      }
      appliedRef.current = null;
      toast(finishSummary(done));
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      leave();
      reload();
      return true;
    } catch {
      setError(
        wrote
          ? 'The changes are in the forma draft, but the review was not recorded. Check your connection, then Finish again.'
          : 'Could not apply the changes. Nothing was changed — try Finish again.'
      );
      return false;
    } finally {
      setBusy(false);
    }
  }, [
    canDecide,
    row,
    ydoc,
    current,
    items,
    decisions,
    userName,
    userId,
    service,
    toast,
    leave,
    reload,
    setRows,
    setError,
  ]);

  return {
    loading: core.loading,
    loaded: core.loaded,
    loadError: core.loadError,
    rows: core.rows,
    upstreamPending: core.upstreamPending,
    pendingCount: pendingChangeCount(core.rows),
    reload,
    active: core.active,
    start: core.start,
    leave,
    row,
    items: formaItems,
    reconciled: formaReconciled,
    decisions,
    walker: core.walker,
    canDecide,
    decide: core.decide,
    acceptAllRemaining: core.acceptAllRemaining,
    canUndoAcceptAll: core.canUndoAcceptAll,
    undoAcceptAll: core.undoAcceptAll,
    finish,
    busy,
    error: core.error,
  };
}
